import type { SessionEvent } from '@orgus/protocol';
import { ChevronLeft, ChevronRight, FilePen, Pause, Play, SkipBack } from '@orgus/ui/icons';
import { useEffect, useMemo, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { Badge, DiffViewer, IconButton, Segmented, baseName, cn, formatClock, formatDuration, formatInt } from '@orgus/ui';
import { EventStatus, KIND_SINGULAR, KIND_TICK, KindIcon } from './EventLook.tsx';
import { REPLAY_SPEEDS, buildFrames, editDiffFile, stepDelay, trackPositions } from './model.ts';
import type { ReplaySpeed } from './model.ts';

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex flex-col">
      <span className="text-caption text-fg-subtle">{label}</span>
      <span className="text-base font-medium text-fg num">{formatInt(value)}</span>
    </div>
  );
}

/**
 * The timeline scrubber: replay a session's steps like a video. Drag the track or use the
 * arrow keys to move one step, Space to play or pause. Each step shows what happened, and
 * file edits show as a diff.
 */
export function Replay({ events }: { events: SessionEvent[] }) {
  const frames = useMemo(() => buildFrames(events), [events]);
  const pos = useMemo(() => trackPositions(events), [events]);
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<ReplaySpeed>(4);
  const last = frames.length - 1;
  const idx = Math.min(i, Math.max(0, last));
  const f = frames[idx];

  useEffect(() => {
    if (!playing) return;
    if (idx >= last) {
      setPlaying(false);
      return;
    }
    const t = setTimeout(() => setI(idx + 1), stepDelay(frames[idx]?.event, frames[idx + 1]?.event, speed));
    return () => clearTimeout(t);
  }, [playing, idx, last, frames, speed]);

  if (!f) return null;
  const e = f.event;
  const go = (n: number) => {
    setPlaying(false);
    setI(Math.max(0, Math.min(last, n)));
  };
  const onKey = (k: KeyboardEvent<HTMLDivElement>) => {
    if ((k.target as HTMLElement).tagName === 'INPUT' && (k.key === 'ArrowLeft' || k.key === 'ArrowRight')) return;
    if (k.key === ' ') {
      k.preventDefault();
      if (idx >= last) setI(0);
      setPlaying((p) => !p);
    } else if (k.key === 'ArrowRight') {
      k.preventDefault();
      go(idx + 1);
    } else if (k.key === 'ArrowLeft') {
      k.preventDefault();
      go(idx - 1);
    } else if (k.key === 'Home') go(0);
    else if (k.key === 'End') go(last);
  };

  return (
    <div className="flex flex-col gap-4" onKeyDown={onKey}>
      <div className="rounded-card border border-border bg-raised p-3">
        <div className="relative h-8">
          <div aria-hidden className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-surface" />
          <div aria-hidden className="absolute left-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-accent/60" style={{ width: `${(pos[idx] ?? 0) * 100}%` }} />
          {events.map((ev, n) => (
            <span
              key={`${ev.seq}-${n}`}
              aria-hidden
              className={cn('absolute top-1/2 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded-full', KIND_TICK[ev.kind], ev.kind === 'prompt' || ev.kind === 'error' || ev.kind === 'approval' ? 'h-4' : 'h-2.5')}
              style={{ left: `${(pos[n] ?? 0) * 100}%` }}
            />
          ))}
          <span
            aria-hidden
            className="pointer-events-none absolute top-1/2 size-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-raised bg-accent shadow-md"
            style={{ left: `${(pos[idx] ?? 0) * 100}%` }}
          />
          <input
            type="range"
            min={0}
            max={last}
            step={1}
            value={idx}
            onChange={(x) => go(Number(x.target.value))}
            aria-label="Replay position"
            aria-valuetext={`Step ${idx + 1} of ${frames.length}: ${e.title}`}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          />
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-1">
          <IconButton label="Back to the start" icon={<SkipBack />} onClick={() => go(0)} disabled={idx === 0} shortcut={['Home']} />
          <IconButton label="Previous step" icon={<ChevronLeft />} onClick={() => go(idx - 1)} disabled={idx === 0} shortcut={['←']} />
          <IconButton
            label={playing ? 'Pause' : 'Play'}
            icon={playing ? <Pause /> : <Play />}
            variant="secondary"
            shortcut={['Space']}
            onClick={() => {
              if (idx >= last) setI(0);
              setPlaying((p) => !p);
            }}
          />
          <IconButton label="Next step" icon={<ChevronRight />} onClick={() => go(idx + 1)} disabled={idx >= last} shortcut={['→']} />
          <span className="ml-2 text-ui text-fg-muted num" aria-live="polite">
            Step {formatInt(idx + 1)} of {formatInt(frames.length)}
          </span>
          {f.elapsed != null && <span className="text-caption text-fg-subtle num">{f.elapsed > 0 ? `${formatDuration(f.elapsed)} in` : 'Start'}</span>}
          <Segmented<string>
            className="ml-auto"
            label="Replay speed"
            value={String(speed)}
            onChange={(v) => setSpeed(Number(v) as ReplaySpeed)}
            options={REPLAY_SPEEDS.map((s) => ({ value: String(s), label: `${s}x` }))}
          />
        </div>
      </div>

      <section aria-label="Current step" className="flex flex-col gap-2 rounded-card border border-border bg-raised p-3">
        <div className="flex items-center gap-2">
          <KindIcon kind={e.kind} className="size-4" />
          <span className="text-ui font-medium text-fg-muted">{KIND_SINGULAR[e.kind]}</span>
          {e.meta && <Badge>{e.meta}</Badge>}
          <EventStatus e={e} />
          {e.durationMs != null && e.durationMs >= 1000 && <span className="text-caption text-fg-subtle num">took {formatDuration(e.durationMs)}</span>}
          <span className="ml-auto text-caption text-fg-subtle num">{e.at != null ? formatClock(e.at) : 'No time recorded'}</span>
        </div>
        <p className={cn('whitespace-pre-wrap break-words text-ui', e.kind === 'error' ? 'text-status-danger' : 'text-fg')}>{e.title}</p>
        {e.detail && !e.edit && <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-control bg-surface px-3 py-2 font-mono text-caption text-fg-muted">{e.detail}</pre>}
        {e.edit && <DiffViewer files={[editDiffFile(e.edit)]} bare />}
      </section>

      <div className="grid grid-cols-4 gap-3 rounded-card border border-border-subtle p-3">
        <Stat label="Prompts" value={f.prompts} />
        <Stat label="Tool calls" value={f.tools} />
        <Stat label="File edits" value={f.edits} />
        <Stat label="Failures" value={f.errors} />
      </div>
      {f.files.length > 0 && (
        <div className="flex flex-col gap-2">
          <h4 className="text-caption font-medium text-fg-subtle">Files edited so far</h4>
          <ul className="flex flex-wrap gap-1">
            {f.files.map((p) => (
              <li key={p} title={p} className="inline-flex h-control-xs items-center gap-1 rounded-control bg-hover px-2 font-mono text-caption text-fg-muted">
                <FilePen className="size-4 text-fg-subtle" aria-hidden />
                {baseName(p)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
